import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import type { Href } from 'expo-router';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useCustomer } from '@/api/customers/use-customer';
import { useLoginCustomerApple } from '@/api/customers/use-login-customer-apple';
import { useLoginCustomerGoogle } from '@/api/customers/use-login-customer-google';
import type { StartOtpResponse } from '@/api/customers/use-start-otp';
import { useStartOtp } from '@/api/customers/use-start-otp';
import { AppleLogin } from '@/components/apple-login/apple-login';
import { EntryEmailForm } from '@/components/entry/email-form';
import { GoogleLogin } from '@/components/google-login';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop, signIn, useAuth, validatePhoneNumber } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useCurrentOrganization } from '@/core/organization';
import { openUrl } from '@/core/utils';
import useSettingsStore from '@/stores/use-settings-store';
import { GREY_90 } from '@/style/colors';
import { Button, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { AvgPayoutIcon } from '@/ui/icons/avg-payout';
import { CoverageIcon } from '@/ui/icons/coverage';
import { InstantUnlockIcon } from '@/ui/icons/instant-unlock';
import { KeylessIcon } from '@/ui/icons/keyless';
import { Mail } from '@/ui/icons/mail';
import { OtherListingsIcon } from '@/ui/icons/other-listings';
import { SelfDrivingIcon } from '@/ui/icons/self-driving';
import { Loader } from '@/ui/loader';

import { EntryConfirmOtp } from './confirm-otp';
import { EntryFinishSignup } from './finish-signup';

const heroImageCustomer = require('../../../public/images/entry/eon-login-customer.png');
const heroImageOwner = require('../../../public/images/entry/eon-login-owner.png');
const heroImageCustomerMobile = require('../../../public/images/entry/eon-login-customer-mobile.png');
const heroImageOwnerMobile = require('../../../public/images/entry/eon-login-owner-mobile.png');
const logoDark = require('../../../public/images/logos/partner-logo-dark.png');
const logoLight = require('../../../public/images/logos/partner-logo-light.png');

const TERMS_URL = 'https://www.eonrides.com/legal/rental-terms';
const PRIVACY_URL = 'https://www.eonrides.com/legal/terms-privacy-policy';

export type EntryVariant = 'renter' | 'owner';

interface EntryScreenProps {
  variant: EntryVariant;
}

const perksByVariant = {
  renter: [
    {
      Icon: InstantUnlockIcon,
      width: 13,
      height: 20,
      tx: 'entry.renter.perkInstantUnlock' as const,
    },
    {
      Icon: KeylessIcon,
      width: 13,
      height: 20,
      tx: 'entry.renter.perkKeyless' as const,
    },
    {
      Icon: SelfDrivingIcon,
      width: 20,
      height: 20,
      tx: 'entry.renter.perkSelfDriving' as const,
    },
  ],
  owner: [
    {
      Icon: AvgPayoutIcon,
      width: 12,
      height: 20,
      tx: 'entry.owner.perkAvgPayout' as const,
    },
    {
      Icon: CoverageIcon,
      width: 17,
      height: 20,
      tx: 'entry.owner.perkCoverage' as const,
    },
    {
      Icon: OtherListingsIcon,
      width: 20,
      height: 16,
      tx: 'entry.owner.perkOtherListings' as const,
    },
  ],
} as const;

const copyByVariant = {
  renter: {
    heroImage: heroImageCustomer,
    heroImageMobile: heroImageCustomerMobile,
    cardTitleTx: 'entry.cardTitle' as const,
    heroTitleTx: 'entry.renter.heroTitle' as const,
    heroSubtitleTx: 'entry.renter.heroSubtitle' as const,
    switchPromptTx: 'entry.renter.switchPrompt' as const,
    switchLinkTx: 'entry.renter.switchLink' as const,
    switchHref: '/partner/login' as const,
  },
  owner: {
    heroImage: heroImageOwner,
    heroImageMobile: heroImageOwnerMobile,
    cardTitleTx: 'entry.owner.cardTitle' as const,
    heroTitleTx: 'entry.owner.heroTitle' as const,
    heroSubtitleTx: 'entry.owner.heroSubtitle' as const,
    switchPromptTx: 'entry.owner.switchPrompt' as const,
    switchLinkTx: 'entry.owner.switchLink' as const,
    switchHref: '/login' as const,
  },
} as const;

export function EntryScreen({ variant }: EntryScreenProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  // Set by callers (e.g. the car booking flow) that want the user dropped back
  // onto a specific screen after signing in, instead of the default landing.
  const { returnTo } = useLocalSearchParams<{ returnTo?: string }>();
  const returnToParam = typeof returnTo === 'string' ? returnTo : undefined;

  // Purchase flows can't proceed without a completed profile, so we hide the
  // "set up later" skip when the user is headed to one. Other returnTo targets
  // (e.g. a guard redirect from /trips or /account) don't require it, so skip
  // stays available there.
  const returnToRequiresProfile =
    !!returnToParam &&
    ['/checkout', '/subscriptions'].some((prefix) =>
      returnToParam.startsWith(prefix)
    );
  const copy = copyByVariant[variant];
  const perks = perksByVariant[variant];

  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>();
  const [showConfirmOtp, setShowConfirmOtp] = useState(false);
  const [showSigninEmailForm, setShowSigninEmailForm] = useState(false);
  const [showFinishSignup, setShowFinishSignup] = useState(false);
  const [isFinishingUp, setIsFinishingUp] = useState(false);

  const refCode = useSettingsStore((state) => state.refCode);
  const promoCode = useSettingsStore((state) => state.promoCode);
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);
  const { slug: organizationSlug } = useCurrentOrganization();

  const status = useAuth.use.status();
  const { data: customer, refetch: refetchCustomer } = useCustomer();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign In',
    });
  }, [variant]);

  useEffect(() => {
    if (status === 'signIn' && customer && !customer.setupComplete) {
      setShowFinishSignup(true);
    }
  }, [status, customer]);

  const { mutate: loginCustomerGoogle, isPending: isLoggingInCustomerGoogle } =
    useLoginCustomerGoogle();

  const { mutate: loginCustomerApple, isPending: isLoggingInCustomerApple } =
    useLoginCustomerApple();

  const { mutate: startOtp, isPending: isStartingOtp } = useStartOtp();

  const isValidPhoneNumber = useMemo(() => {
    if (!phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  const handleDone = async () => {
    // Keep showing a loader while we recheck the customer and navigate away,
    // otherwise the entry card briefly flashes back into view once the
    // finish-signup/OTP step clears itself before the redirect resolves.
    setIsFinishingUp(true);

    // Recheck against the freshly logged-in customer rather than the entry
    // page's variant (URL) or a stale persisted partnerMode flag, since an
    // admin/host account should always land in partner mode, and a
    // non-admin should never inherit partner mode left over from a
    // previous account on this device.
    const { data: freshCustomer } = await refetchCustomer();
    const isPartner = Boolean(freshCustomer?.partnerToken);

    // A non-admin who signed in through the owner entry has no partner account
    // yet, so they must enroll before anything else. This wins even over a
    // returnTo, since the guard that sent them here would have set returnTo to a
    // partner route they can't access until they've joined the program.
    if (variant === 'owner' && !isPartner) {
      setPartnerMode(false);
      router.replace('/join-partner-program');
      return;
    }

    // Purchase flows (a car booking -> /checkout, or /subscriptions) must
    // resume exactly where they left off, even for an admin: yanking a
    // mid-booking admin over to the partner app would abandon their cart. Stay
    // in renter mode so the (app) layout's native partner-mode guard doesn't
    // bounce them to /partner/dashboard mid-flow.
    if (returnToParam && cameFromPurchaseFlow) {
      setPartnerMode(false);
      router.replace(returnToParam as Href);
      return;
    }

    // An explicit partner destination (a guard bounced them off a /partner
    // route) enters the partner app.
    if (returnToParam && returnToParam.startsWith('/partner')) {
      setPartnerMode(true);
      router.replace(returnToParam as Href);
      return;
    }

    // Admins otherwise always land in the partner app, regardless of which
    // renter-side screen (/account, /trips, ...) they signed in from — being an
    // admin is the signal that the partner app is their home base.
    if (isPartner) {
      setPartnerMode(true);
      router.replace('/partner/dashboard');
      return;
    }

    // Non-admins go back to wherever a guard sent them from (e.g. /account,
    // /trips), or the public default when there's no returnTo.
    setPartnerMode(false);
    router.replace((returnToParam ?? '/search') as Href);
  };

  const handlePhoneSubmit = () => {
    const formattedPhone = `+${country?.callingCode}${phone}`;

    startOtp(
      { phone: formattedPhone },
      {
        onSuccess: (_: StartOtpResponse) => {
          setShowConfirmOtp(true);
          setPhone(formattedPhone);
        },
        onError: (e) => showErrorMessage(e.message),
      }
    );
  };

  const handleGoogleLogin = (credential: string) => {
    loginCustomerGoogle(
      {
        googleCredential: credential,
        referralCode: refCode,
        promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');
          Toast.show({ text1: 'Signed in successfully', type: 'success' });
          signIn(respData.data.token);

          if (respData.data.setupComplete) {
            handleDone();
          } else {
            setShowFinishSignup(true);
          }
        },
        onError: (e) => {
          mixpanelService.track('Sign In Failed');
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleAppleLogin = (
    userId: string,
    identityToken: string,
    fullName: string
  ) => {
    loginCustomerApple(
      {
        userId,
        identityToken,
        fullName,
        referralCode: refCode,
        promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');
          Toast.show({ text1: 'Signed in successfully', type: 'success' });
          signIn(respData.data.token);

          if (respData.data.setupComplete) {
            handleDone();
          } else {
            setShowFinishSignup(true);
          }
        },
        onError: (e) => {
          mixpanelService.track('Sign In Failed');
          showErrorMessage(e.message);
        },
      }
    );
  };

  // Purchase flows (a car booking -> /checkout, or /subscriptions) push the
  // login screen on top of a public page, so the entry beneath us in the stack
  // is that same public page — popping back to it is safe and expected.
  const SAFE_BACK_PREFIXES = ['/checkout', '/subscriptions', '/car'];
  const cameFromPurchaseFlow =
    !!returnToParam &&
    SAFE_BACK_PREFIXES.some((prefix) => returnToParam.startsWith(prefix));

  const handleBack = () => {
    // Only pop the stack when the user came from a public purchase flow.
    // Anything else (trips, account, or any partner route) reached login via a
    // guard that *replaced* a protected route, so the entries still underneath
    // are protected too — popping to one just bounces us right back to login,
    // which is the infinite loop. For those, exit to the public search page.
    if (cameFromPurchaseFlow && router.canGoBack()) {
      router.back();
      return;
    }

    router.replace('/search');
  };

  // Back from the finish-profile step is its own flow, distinct from the entry
  // card's handleBack. The user has already authenticated here, so "back"
  // means "leave without finishing my profile":
  //   - Purchase flow (car/subscriptions): pop back to the booking screen the
  //     login was pushed on top of, so they can resume and tap checkout again.
  //   - Everything else (account/trips/plain login): behave like "set up
  //     later" and continue to where they were headed (returnTo), rather than
  //     dumping them on /search — an incomplete profile is fine for those.
  const handleFinishSignupBack = () => {
    if (cameFromPurchaseFlow) {
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace('/search');
      }
      return;
    }

    handleDone();
  };

  const onFinishSignupDone = (newPhone?: string) => {
    setShowFinishSignup(false);

    if (newPhone) {
      setShowConfirmOtp(true);
      setPhone(newPhone);
    } else {
      handleDone();
    }
  };

  if (
    isLoggingInCustomerGoogle ||
    isLoggingInCustomerApple ||
    isStartingOtp ||
    isFinishingUp
  ) {
    return (
      <View style={styles.loaderContainer}>
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  // Admin/host accounts always get the host profile form, even if they
  // logged in through the renter entry page.
  const finishSignupVariant: EntryVariant = customer?.partnerToken
    ? 'owner'
    : variant;

  const renderCard = () => {
    if (showFinishSignup) {
      return (
        <EntryFinishSignup
          onDone={onFinishSignupDone}
          onBack={handleFinishSignupBack}
          onSkip={
            // Renters can skip unless they're headed to a purchase flow that
            // needs a completed profile (/checkout, /subscriptions). Owners
            // must always finish. Skipping honors returnTo via handleDone.
            finishSignupVariant === 'renter' && !returnToRequiresProfile
              ? handleDone
              : undefined
          }
        />
      );
    }

    if (showConfirmOtp) {
      return (
        <EntryConfirmOtp
          phone={phone}
          onBack={() => setShowConfirmOtp(false)}
          onDone={(setupComplete: boolean) => {
            setShowConfirmOtp(false);

            if (setupComplete) {
              handleDone();
            } else {
              setShowFinishSignup(true);
            }
          }}
        />
      );
    }

    if (showSigninEmailForm) {
      return (
        <EntryEmailForm
          onBack={() => setShowSigninEmailForm(false)}
          onDone={(setupComplete: boolean) => {
            setShowSigninEmailForm(false);

            if (setupComplete) {
              handleDone();
            } else {
              setShowFinishSignup(true);
            }
          }}
        />
      );
    }

    return (
      <View style={styles.card}>
        {isDesktop() && (
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={handleBack}
          />
        )}

        <View style={styles.cardTitleContainer}>
          <Text tx={copy.cardTitleTx} type="title1" />
        </View>

        <View style={styles.phoneRow}>
          <InputPhone
            onChangeText={setPhone}
            onCountryChange={setCountry}
            placeholder={t('entry.phonePlaceholder')}
            style={{ width: '100%' }}
            countryLabel={
              Platform.OS === 'web' ? t('entry.countryLabel') : undefined
            }
            phoneLabel={
              Platform.OS === 'web' ? t('entry.phoneLabel') : undefined
            }
            onSubmitEditing={() => {
              if (!isValidPhoneNumber) return;

              mixpanelService.track('Button Clicked', {
                'Button Name': 'Sign In Phone',
              });

              handlePhoneSubmit();
            }}
          />

          <Button
            tx="signin.continue"
            type="primary"
            onPress={() => {
              mixpanelService.track('Button Clicked', {
                'Button Name': 'Sign In Phone',
              });

              handlePhoneSubmit();
            }}
            disabled={!isValidPhoneNumber}
          />
        </View>

        <View style={styles.dividerRow}>
          <View style={styles.dividerLine} />
          <Text tx="signin.or" type="bodyRegular" style={styles.cardSubtitle} />
          <View style={styles.dividerLine} />
        </View>

        <View style={{ gap: 16 }}>
          {!organizationSlug ? (
            <>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                tx="entry.continueGoogle"
                onPress={() => {
                  mixpanelService.track('Button Clicked', {
                    'Button Name': 'Sign In Google',
                  });
                }}
              />
              <AppleLogin
                tx="entry.continueApple"
                onSuccess={handleAppleLogin}
                onPress={() => {
                  mixpanelService.track('Button Clicked', {
                    'Button Name': 'Sign In Apple',
                  });
                }}
              />
            </>
          ) : null}

          <Button
            leftIcon={
              <Icon
                type="naked"
                icon={<Mail />}
                fillColor={theme.colors.icon.nakedColor}
                width={20}
                height={20}
              />
            }
            tx="entry.continueEmail"
            type="secondary"
            onPress={() => {
              mixpanelService.track('Button Clicked', {
                'Button Name': 'Sign In Email Form',
              });

              setShowSigninEmailForm(true);
            }}
          />
        </View>

        <View style={styles.switchRow}>
          <Text
            tx={copy.switchPromptTx}
            type="caption1"
            style={styles.cardSubtitle}
          />
          <Text
            tx={copy.switchLinkTx}
            type="caption1Bold"
            style={[styles.cardSubtitle, styles.switchLink]}
            onPress={() =>
              router.replace(
                // Preserve returnTo when toggling between the renter and owner
                // login so a booking flow (e.g. /checkout) still resumes after
                // signing in on the other side.
                (returnToParam
                  ? `${copy.switchHref}?returnTo=${encodeURIComponent(
                      returnToParam
                    )}`
                  : copy.switchHref) as Href
              )
            }
          />
        </View>

        <View style={styles.legalRow}>
          <Text
            tx="entry.legal.agreePrefix"
            type="caption1"
            style={styles.legalText}
          />

          <View style={styles.legalLinksRow}>
            <Text
              tx="entry.legal.termsAndConditions"
              type="caption1"
              style={[styles.legalText, styles.legalLink]}
              onPress={() => openUrl(TERMS_URL)}
            />
            <Text
              tx="entry.legal.and"
              type="caption1"
              style={styles.legalText}
            />
            <Text
              tx="entry.legal.privacyPolicy"
              type="caption1"
              style={[styles.legalText, styles.legalLink]}
              onPress={() => openUrl(PRIVACY_URL)}
            />
          </View>
        </View>
      </View>
    );
  };

  const isMainCard = !(
    showConfirmOtp ||
    showFinishSignup ||
    showSigninEmailForm
  );
  // Desktop keeps the hero as a permanent side panel. Mobile/native only shows
  // it above the main entry card, since the sub-forms (OTP, finish-signup,
  // email) are tall enough to want the whole screen.
  const showHero = isDesktop() || isMainCard;
  // The main card hides its own back button on mobile, so float one over the
  // hero artwork instead. Sub-forms render their own back affordance.
  const showHeroBack = !isDesktop() && isMainCard;

  return (
    <View style={styles.page}>
      {showHero && (
        <View style={styles.heroPanel}>
          <Image
            source={isDesktop() ? copy.heroImage : copy.heroImageMobile}
            style={styles.heroImage}
            contentFit="cover"
            contentPosition={
              isDesktop() ? 'top' : Platform.OS === 'web' ? 'center' : 'top'
            }
          />

          {isDesktop() && (
            <>
              <LinearGradient
                // Keep the photo clear and only fade to black across the
                // bottom, so the copy stays legible without dimming the whole
                // image.
                colors={[
                  'rgba(8,6,6,0)',
                  'rgba(8,6,6,0)',
                  'rgba(8,6,6,0.9)',
                  GREY_90,
                ]}
                locations={[0, 0.8, 0.9, 1]}
                style={styles.heroGradient}
              />

              <Image
                source={logoDark}
                style={styles.heroLogo}
                contentFit="contain"
              />

              <View style={styles.heroCopy}>
                <Text
                  tx={copy.heroTitleTx}
                  type="largeTitle"
                  style={styles.heroTitle}
                />
                <Text
                  tx={copy.heroSubtitleTx}
                  type="bodyRegular"
                  style={[styles.heroSubtitle, { maxWidth: 800 }]}
                />

                <View style={styles.perksRow}>
                  {perks.map((perk) => (
                    <View key={perk.tx} style={styles.perkItem}>
                      <View style={{ width: perk.width, height: perk.height }}>
                        <perk.Icon />
                      </View>
                      <Text
                        tx={perk.tx}
                        type="bodyBold"
                        style={styles.perkLabel}
                      />
                    </View>
                  ))}
                </View>
              </View>
            </>
          )}

          {showHeroBack && (
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={handleBack}
              style={styles.heroBackButton}
            />
          )}
        </View>
      )}

      <View style={[styles.formPanel, showHero && styles.formPanelOverlap]}>
        <KeyboardAwareScrollView
          style={{ width: '100%' }}
          contentContainerStyle={[
            styles.formScrollContent,
            !showHero && styles.formScrollContentTop,
          ]}
          showsVerticalScrollIndicator={false}
        >
          {renderCard()}
        </KeyboardAwareScrollView>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  page: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    backgroundColor: theme.colors.background_1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  heroPanel: {
    width: {
      xs: '100%',
      md: '50%',
    },
    aspectRatio: {
      xs: isDesktop() ? 1.6 : Platform.OS === 'web' ? 2 : 1.4,
      md: undefined,
    },
    height: {
      md: '100%',
    },
    position: 'relative',
    overflow: 'hidden',
  },
  heroImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heroGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heroBackButton: {
    position: 'absolute',
    top: UnistylesRuntime.insets.top + 16,
    left: 16,
  },
  heroCopy: {
    position: 'absolute',
    left: 48,
    right: 48,
    bottom: 64,
    gap: 24,
  },
  heroLogo: {
    position: 'absolute',
    top: 48,
    left: 48,
    width: 110,
    height: 46,
  },
  heroTitle: {
    color: '#FFFFFF',
  },
  heroSubtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  perksRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 24,
  },
  perkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  perkLabel: {
    color: '#FFFFFF',
  },
  formPanel: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: {
      md: 1,
    },
    borderLeftColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.background_1,
  },
  formPanelOverlap: {
    marginTop: {
      xs: -24,
      md: 0,
    },
    borderTopLeftRadius: {
      xs: 20,
      md: 0,
    },
    borderTopRightRadius: {
      xs: 20,
      md: 0,
    },
  },
  formScrollContent: {
    flexGrow: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: {
      md: 'center',
    },
    paddingTop: {
      xs: 10,
      md: 40,
    },
    paddingBottom: {
      xs: UnistylesRuntime.insets.bottom + 8,
      md: 40,
    },
  },
  formScrollContentTop: {
    justifyContent: 'flex-start',
    paddingTop: UnistylesRuntime.insets.top + 26,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    paddingHorizontal: 18,
    gap: 16,
    // Stretch to the full scroll viewport on mobile so the legal row can be
    // pushed to the bottom edge. Desktop centers the card instead.
    flexGrow: {
      xs: 1,
      md: 0,
    },
  },
  cardTitleContainer: {
    gap: 8,
    marginTop: 8,
  },
  cardLogo: {
    width: 90,
    height: 38,
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  phoneRow: {
    gap: 12,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  switchLink: {
    textDecorationLine: 'underline',
  },
  legalRow: {
    // Soak up any leftover height on mobile so the legal copy sits against the
    // bottom of the screen rather than trailing the sign-in buttons.
    marginTop: {
      xs: 'auto',
      md: 0,
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: {
      xs: 'nowrap',
      md: 'wrap',
    },
    gap: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  legalLinksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    justifyContent: 'center',
  },
  legalText: {
    color: theme.colors.text.secondary,
  },
  legalLink: {
    textDecorationLine: 'underline',
  },
}));
